/**
 * The requests a composer send turns into — the rules of the CRM chat window
 * (chats.js sendMessage / buildChatPaidMessageBody / sendChatPendingGif):
 * - text only → /v1/messages/send;
 * - any media or a price → /v1/messages/send-with-content; free media goes
 *   out as free previews, a PPV lists only its free-preview items in `previews`;
 * - a voice note is always free and goes out as OnlyFans' transcoder handoff in
 *   `media_files` of /v1/messages/send (it is in no vault before it is sent):
 *   with the text when nothing else is in the draft, else as a message of its
 *   own right after the main one;
 * - a price with text and no media is a paid (locked) text;
 * - a GIF is OnlyFans' own chat message (giphyId): alone it carries the text,
 *   next to other content it follows as a second message.
 */

export const PRICE_MIN = 3;
export const PRICE_MAX = 200;

export interface DraftMedia {
  id: string;
  /** Free preview inside a PPV. */
  preview?: boolean;
  /** Recorded voice note (always free). */
  voice?: boolean;
  /** A voice note's transcoder handoff ({processId, host, …}), sent in place of a vault id. */
  handoff?: Record<string, unknown>;
  /** For the pending copy in the Transcript only (never sent). */
  type?: 'photo' | 'video' | 'audio' | 'gif';
  thumb?: string | null;
}

export interface Draft {
  fanId: string;
  text: string;
  media: DraftMedia[];
  /** PPV price in dollars, or null for a free message. */
  price: number | null;
  /** The message this one answers (quoted above the text, as the CRM does). */
  replyTo?: { text: string } | null;
  /** Giphy id of a GIF to send. */
  giphyId?: string | null;
}

export type SendRefusal = 'empty' | 'price_range' | 'media_not_ready' | 'paid_text_required';

export interface SendRequest {
  path: '/v1/messages/send' | '/v1/messages/send-with-content' | '/v1/of/raw';
  body: Record<string, unknown>;
}

export type BuiltSend = { ok: true; requests: SendRequest[] } | { ok: false; reason: SendRefusal };

const isMediaId = (id: string) => /^\d{4,}$/.test(String(id));

/** The text that goes out: a reply quotes the answered message on its first line. */
export function outgoingText(text: string, replyTo?: { text: string } | null): string {
  const message = text.trim();
  if (!message || !replyTo) return message;
  return `↪ ${replyTo.text.slice(0, 120)}\n${message}`;
}

function gifRequest(fanId: string, text: string, giphyId: string): SendRequest {
  return {
    path: '/v1/of/raw',
    body: {
      method: 'POST',
      api_path: `/api2/v2/chats/${fanId}/messages`,
      referer: `https://onlyfans.com/my/chats/chat/${fanId}/`,
      body: { text, giphyId },
    },
  };
}

export function buildSend({ fanId, text, media, price, replyTo, giphyId }: Draft): BuiltSend {
  const message = outgoingText(text, replyTo);
  const gif = giphyId ? String(giphyId) : '';
  const content = media.filter((m) => !m.voice);
  const voices = media.filter((m) => m.voice);
  const priced = price != null && price > 0;
  const paid = priced && (content.length > 0 || !!message);

  if (!message && !media.length && !gif) return { ok: false, reason: 'empty' };
  if (priced && (price < PRICE_MIN || price > PRICE_MAX)) return { ok: false, reason: 'price_range' };
  if (content.some((m) => !isMediaId(m.id))) return { ok: false, reason: 'media_not_ready' };
  if (voices.some((m) => !m.handoff)) return { ok: false, reason: 'media_not_ready' };
  // One free preview and nothing else locked: the text is what the fan pays for.
  if (paid && content.length === 1 && content[0]!.preview && !message) return { ok: false, reason: 'paid_text_required' };

  if (gif && !media.length && !paid) return { ok: true, requests: [gifRequest(fanId, message, gif)] };

  const voiceFiles = voices.map((m) => m.handoff!);
  const requests: SendRequest[] = [];
  if (voiceFiles.length && !content.length && !paid) {
    // Voice (and its text) alone: one message, as the AI sends its voice notes.
    requests.push({ path: '/v1/messages/send', body: { fan_id: fanId, message, media_files: voiceFiles } });
  } else {
    if (!content.length && !paid) {
      requests.push({ path: '/v1/messages/send', body: { fan_id: fanId, message } });
    } else {
      const previews = paid ? content.filter((m) => m.preview) : content;
      const locked = paid ? content.filter((m) => !m.preview) : [];
      const ids = content.map((m) => Number(m.id));
      const first = locked[0] || previews[0];
      const body: Record<string, unknown> = {
        fan_id: fanId,
        message: message || ' ',
        content_id: first ? String(first.id) : null,
        price: paid ? Math.round(price! * 100) / 100 : 0,
      };
      if (ids.length) {
        body.media_files = ids;
        body.previews = previews.map((m) => Number(m.id));
      }
      requests.push({ path: '/v1/messages/send-with-content', body });
    }
    if (voiceFiles.length) requests.push({ path: '/v1/messages/send', body: { fan_id: fanId, message: '', media_files: voiceFiles } });
  }
  if (gif) requests.push(gifRequest(fanId, '', gif));
  return { ok: true, requests };
}
